import {
  Card,
  Col,
  Descriptions,
  DatePicker,
  Flex,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Typography,
} from 'antd';
import type { AssetDetail } from '~/app/services/asset/asset.service';
import type { AssetModelMetadata } from '@evimo/types';
import { useTranslation } from 'react-i18next';
import type { FormInstance } from 'antd/es/form';
import type { AssetDetailFormValues } from '~/app/hooks/asset/useAssetDetail';
import type { AssetTypeMetadata } from '~/app/services/asset-type/asset-type.service';
import type { SiteLookup, ZoneLookup } from '~/app/services/site/site.service';
import type { FindAssetCustomFieldResponseDto } from '@evimo/types/api/asset-custom-field/find-asset-custom-field/find-asset-custom-field.response.dto';
import { AssetCustomFieldsFormSection } from '~/app/components/asset-custom-fields/AssetCustomFieldsFormSection';
import { formatDateTime } from './assetDetail.utils';
import { AssetLocationMap } from './AssetLocationMap';
import { useAssetDetailOverviewMapCoords } from '~/app/components/asset-detail/hooks/useAssetDetailOverviewMapCoords';
import { useAssetStandardOptions } from '~/app/hooks/asset/useAssetStandardOptions';
import { ASSET_DETAIL_LOCATION_SECTION_ID } from './assetDetailTabs';
import styles from './AssetDetailOverviewTab.module.css';

type Props = {
  asset: AssetDetail;
  form: FormInstance<AssetDetailFormValues>;
  assetTypes: AssetTypeMetadata[];
  assetModels: AssetModelMetadata[];
  manufacturerOptions: Array<{ value: string; label: string }>;
  atmmLoading?: boolean;
  landlordOptions: Array<{ value: string; label: string }>;
  sites: SiteLookup[];
  zones: ZoneLookup[];
  siteForMap: Pick<
    SiteLookup,
    'name' | 'address' | 'latitude' | 'longitude'
  > | null;
  onValuesChange: (changedValues: Record<string, unknown>) => void;
  onCoordinatesChange: (coords: { lat: number; lng: number }) => void;
  customFields: FindAssetCustomFieldResponseDto[];
  customFieldsLoading?: boolean;
};

export function AssetDetailOverviewTab({
  asset,
  form,
  assetTypes,
  assetModels,
  manufacturerOptions,
  atmmLoading = false,
  landlordOptions,
  sites,
  zones,
  siteForMap,
  onValuesChange,
  onCoordinatesChange,
  customFields,
  customFieldsLoading = false,
}: Props) {
  const { t } = useTranslation('assets');
  const standardOptions = useAssetStandardOptions(t);
  const { mapLatitude, mapLongitude } = useAssetDetailOverviewMapCoords(
    form,
    siteForMap,
  );

  const mainZoneLabel =
    typeof asset.rawData?.mainZone === 'string' && asset.rawData.mainZone.trim()
      ? asset.rawData.mainZone.trim()
      : undefined;
  const zonePlaceholder =
    !asset.zone && mainZoneLabel
      ? mainZoneLabel
      : t('detail.placeholders.zone', 'Select a zone');

  return (
    <Flex vertical gap={16} className={styles.root}>
      <Card className={styles.card} bordered={false}>
        <Form
          form={form}
          layout="vertical"
          className={styles.form}
          onValuesChange={onValuesChange}
        >
          <div className={styles.sectionBlock}>
            <Typography.Title level={5} className={styles.sectionTitle}>
              {t('detail.sections.general', 'General information')}
            </Typography.Title>
            <Row gutter={[16, 0]}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="assetName"
                  label={t('detail.fields.assetName', 'Asset name')}
                  rules={[
                    {
                      required: true,
                      message: t(
                        'detail.validation.assetName',
                        'Asset name is required',
                      ),
                    },
                  ]}
                >
                  <Input
                    placeholder={t(
                      'detail.placeholders.assetName',
                      'Enter asset name',
                    )}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="serialNumber"
                  label={t('detail.fields.serialNumber', 'Serial number')}
                  rules={[
                    {
                      required: true,
                      message: t(
                        'detail.validation.serialNumber',
                        'Serial number is required',
                      ),
                    },
                  ]}
                >
                  <Input
                    placeholder={t(
                      'detail.placeholders.serialNumber',
                      'Enter serial number',
                    )}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="manufacturerId"
                  label={t('detail.fields.manufacturer', 'Manufacturer')}
                >
                  <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    loading={atmmLoading}
                    placeholder={t(
                      'detail.placeholders.manufacturer',
                      'Select manufacturer organization',
                    )}
                    options={manufacturerOptions}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="assetTypeId"
                  label={t('detail.fields.assetType', 'Asset type')}
                  rules={[
                    {
                      required: true,
                      message: t(
                        'detail.validation.assetType',
                        'Asset type is required',
                      ),
                    },
                  ]}
                >
                  <Select
                    showSearch
                    optionFilterProp="label"
                    placeholder={t(
                      'detail.placeholders.assetType',
                      'Select asset type',
                    )}
                    options={assetTypes.map((item) => ({
                      label: item.name,
                      value: item.id,
                    }))}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="assetModelId"
                  label={t('detail.fields.assetModel', 'Asset model')}
                >
                  <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    loading={atmmLoading}
                    placeholder={t(
                      'detail.placeholders.assetModel',
                      'Select asset model',
                    )}
                    options={assetModels.map((item) => ({
                      label: item.name,
                      value: item.id,
                    }))}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="standard"
                  label={t('detail.fields.standard', 'Standard')}
                >
                  <Select
                    allowClear
                    placeholder={t(
                      'detail.placeholders.standard',
                      'Select standard',
                    )}
                    options={standardOptions}
                  />
                </Form.Item>
              </Col>
            </Row>
          </div>

          <div className={styles.sectionBlock}>
            <Typography.Title level={5} className={styles.sectionTitle}>
              {t('detail.sections.lifecycle', 'Lifecycle and ownership')}
            </Typography.Title>
            <Row gutter={[16, 0]}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="installedAt"
                  label={t('detail.fields.installedAt', 'Installed at')}
                >
                  <DatePicker
                    showTime
                    placeholder={t(
                      'detail.placeholders.date',
                      'Select date and time',
                    )}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="commissionedAt"
                  label={t('detail.fields.commissionedAt', 'Commissioned at')}
                >
                  <DatePicker
                    showTime
                    placeholder={t(
                      'detail.placeholders.date',
                      'Select date and time',
                    )}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="decommissionedAt"
                  label={t(
                    'detail.fields.decommissionedAt',
                    'Decommissioned at',
                  )}
                >
                  <DatePicker
                    showTime
                    placeholder={t(
                      'detail.placeholders.date',
                      'Select date and time',
                    )}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="expectedDailyEarning"
                  label={t(
                    'detail.fields.expectedDailyEarning',
                    'Expected earning (€/day)',
                  )}
                >
                  <InputNumber min={0} step={0.01} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="landlordOrganizationId"
                  label={t('detail.fields.landlord', 'Landlord')}
                  extra={
                    landlordOptions.length === 0
                      ? t(
                          'detail.noLandlordLinked',
                          'No landlord linked yet — go to SLA > Landlords first',
                        )
                      : undefined
                  }
                >
                  <Select
                    allowClear
                    showSearch={{ optionFilterProp: 'label' }}
                    disabled={landlordOptions.length === 0}
                    placeholder={t(
                      'detail.placeholders.landlord',
                      'Select linked landlord',
                    )}
                    options={landlordOptions}
                  />
                </Form.Item>
              </Col>
            </Row>
          </div>

          <section
            id={ASSET_DETAIL_LOCATION_SECTION_ID}
            className={styles.locationSection}
          >
            <Typography.Title level={5} className={styles.sectionTitle}>
              {t('detail.sections.location', 'Location')}
            </Typography.Title>

            <div className={styles.locationGrid}>
              <div className={styles.locationFields}>
                <Form.Item
                  name="siteId"
                  label={t('detail.fields.site', 'Site')}
                  rules={[
                    {
                      required: true,
                      message: t('detail.validation.site', 'Site is required'),
                    },
                  ]}
                >
                  <Select
                    showSearch
                    optionFilterProp="label"
                    placeholder={t('detail.placeholders.site', 'Select a site')}
                    options={sites.map((item) => ({
                      label: item.name,
                      value: item.id,
                    }))}
                  />
                </Form.Item>

                <Form.Item
                  name="zoneId"
                  label={t('detail.fields.zone', 'Zone')}
                >
                  <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    placeholder={zonePlaceholder}
                    options={zones.map((item) => ({
                      label: item.name,
                      value: item.id,
                    }))}
                  />
                </Form.Item>

                <Form.Item
                  name="siteAddress"
                  label={t('detail.fields.siteAddress', 'Site address')}
                >
                  <Input
                    placeholder={t(
                      'detail.placeholders.siteAddress',
                      'Enter site address',
                    )}
                  />
                </Form.Item>

                <div className={styles.coordsRow}>
                  <Form.Item
                    name="siteLatitude"
                    label={t('detail.fields.siteLatitude', 'Site latitude')}
                    rules={[
                      {
                        validator: async (_rule, value) => {
                          const normalizedValue = value?.trim() ?? '';

                          if (!normalizedValue) {
                            return;
                          }

                          const parsedValue = Number(normalizedValue);

                          if (
                            Number.isNaN(parsedValue) ||
                            parsedValue < -90 ||
                            parsedValue > 90
                          ) {
                            throw new Error(
                              t(
                                'detail.validation.siteLatitude',
                                'Latitude must be between -90 and 90.',
                              ),
                            );
                          }
                        },
                      },
                    ]}
                  >
                    <Input
                      placeholder={t(
                        'detail.placeholders.siteLatitude',
                        'e.g. 48.8566',
                      )}
                    />
                  </Form.Item>

                  <Form.Item
                    name="siteLongitude"
                    label={t('detail.fields.siteLongitude', 'Site longitude')}
                    rules={[
                      {
                        validator: async (_rule, value) => {
                          const normalizedValue = value?.trim() ?? '';

                          if (!normalizedValue) {
                            return;
                          }

                          const parsedValue = Number(normalizedValue);

                          if (
                            Number.isNaN(parsedValue) ||
                            parsedValue < -180 ||
                            parsedValue > 180
                          ) {
                            throw new Error(
                              t(
                                'detail.validation.siteLongitude',
                                'Longitude must be between -180 and 180.',
                              ),
                            );
                          }
                        },
                      },
                    ]}
                  >
                    <Input
                      placeholder={t(
                        'detail.placeholders.siteLongitude',
                        'e.g. 2.3522',
                      )}
                    />
                  </Form.Item>
                </div>
              </div>

              <div className={styles.mapPane}>
                <AssetLocationMap
                  latitude={mapLatitude}
                  longitude={mapLongitude}
                  title={siteForMap?.name ?? asset.site.name}
                  fillContainer
                  unavailableText={t(
                    'detail.map.unavailable',
                    'No coordinates are available for this site yet.',
                  )}
                  missingKeyText={t(
                    'detail.map.missingKey',
                    'Missing Google Maps API key. Add VITE_PUBLIC_GOOGLE_MAPS_API_KEY to apps/web/.env.',
                  )}
                  onCoordinatesChange={onCoordinatesChange}
                />
              </div>
            </div>
          </section>

          {customFields.length > 0 ? (
            <div className={styles.customFields}>
              <Typography.Title level={5} className={styles.sectionTitle}>
                {t('detail.sections.customFields', 'Custom fields')}
              </Typography.Title>
              <AssetCustomFieldsFormSection
                fields={customFields}
                disabled={customFieldsLoading}
              />
            </div>
          ) : null}
        </Form>

        <div className={styles.audit}>
          <Typography.Title level={5} className={styles.sectionTitle}>
            {t('detail.sections.audit', 'Audit information')}
          </Typography.Title>
          <Descriptions column={{ xs: 1, md: 2, xl: 3 }} size="small">
            <Descriptions.Item
              label={t('detail.fields.createdAt', 'Created at')}
            >
              {formatDateTime(asset.createdAt)}
            </Descriptions.Item>
            <Descriptions.Item
              label={t('detail.fields.updatedAt', 'Updated at')}
            >
              {formatDateTime(asset.updatedAt)}
            </Descriptions.Item>
            <Descriptions.Item label={t('detail.fields.city', 'City')}>
              {asset.site.city?.name ?? '-'}
            </Descriptions.Item>
            <Descriptions.Item
              label={t('detail.fields.postalCode', 'Postal code')}
            >
              {asset.site.postalCode ?? '-'}
            </Descriptions.Item>
          </Descriptions>
        </div>
      </Card>
    </Flex>
  );
}
